import { HttpClient } from "../../http/HttpClient";
import CustomerKYCRepository from "@/core/customers/repositories/CustomerKYCRepository";
import { CustomerKYCResponse } from "@/core/customers/entities/CustomerKYC";
import { CustomerKYCUpdateDTO } from "@/core/customers/entities/CustomerKYCUpdate";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import { getProspectUrl } from "../../api/prospectsBackendEndpoints/prospectsBackendEndpoints";
import { updateCustomerKYCUrl } from "../../api/customersBackendEndpoints/customersBackendEndpoints";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import { CustomerKYCMapper } from "@/core/customers/mappers/CustomerKYCMapper";
import { CustomerKYCUpdateMapper } from "@/core/customers/mappers/CustomerKYCUpdateMapper";
import { ApiProspectByBusinessKeyResponse } from "@/core/customers/mappers/ProspectsPipelineMapper";

export default class CustomerKYCRepositoryBackend implements CustomerKYCRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getCustomerKYC(businessKey: string): Promise<CustomerKYCResponse> {
    try {
      const url = getProspectUrl(businessKey);

      const response = await this.httpClient.get<
        ApiResponse<ApiProspectByBusinessKeyResponse>
      >({
        url,
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<ApiProspectByBusinessKeyResponse>(
          response,
        );

      if (result.success && result.data) {
        const apiResponse = {
          code: response.code,
          message: response.message,
          data: result.data,
        };

        return CustomerKYCMapper.fromProspectApiResponse(apiResponse);
      }

      throw ApiResponseHandler.createExceptionFromResponse(response);
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }

  async updateCustomerKYC(
    businessKey: string,
    updates: Partial<CustomerKYCUpdateDTO>,
  ): Promise<boolean> {
    try {
      const body = CustomerKYCUpdateMapper.toApiDTO(updates);

      const response = await this.httpClient.patch<
        ApiResponse<unknown>,
        object
      >({
        url: updateCustomerKYCUrl(businessKey),
        body,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      return !!result.success;
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }

  async updateAdditionalInformation(
    businessKey: string,
    updates: Pick<
      CustomerKYCUpdateDTO,
      | "situationPatrimoniale"
      | "sourceDesFonds"
      | "objetRelationBancaire"
      | "residenceFiscalePays"
      | "ppe"
      | "fonctionPpeExercee"
      | "beneficiaireEffectif"
      | "identiteBeneficiaireEffectif"
    >,
  ): Promise<boolean> {
    return this.updateCustomerKYC(businessKey, updates);
  }
}
